import type { EcosystemDefinition, EcosystemAdminEntry, EcosystemGroupMember } from '@frontend/types';

let _cache: EcosystemDefinition[] | null = null;

export async function getEcosystems(token: string): Promise<EcosystemDefinition[]> {
  if (_cache) return _cache;
  const res = await fetch('/api/ecosystems', {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to load ecosystems');
  const data = await res.json();
  _cache = data.ecosystems as EcosystemDefinition[];
  return _cache;
}

export function clearEcosystemsCache(): void {
  _cache = null;
}

// ---------------------------------------------------------------------------
// Admin ecosystem management (patriot-admins only)
// ---------------------------------------------------------------------------

async function adminFetch(method: string, path: string, token: string, body?: unknown) {
  const res = await fetch(path, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { message?: string }).message || `Request failed: ${res.status}`);
  return data;
}

export interface AdminPackageAvailability {
  catalog: Array<{ id: string; label: string }>;
  installedServices: string[];
  realmServices: string[];
}

export async function adminGetEcosystems(token: string): Promise<{
  ecosystems: EcosystemAdminEntry[];
  packageAvailability: AdminPackageAvailability;
}> {
  const data = await adminFetch('GET', '/api/admin/ecosystems', token);
  const response = data as {
    ecosystems: EcosystemAdminEntry[];
    packageAvailability?: AdminPackageAvailability;
  };
  return {
    ecosystems: response.ecosystems,
    packageAvailability: response.packageAvailability || {
      catalog: [],
      installedServices: [],
      realmServices: [],
    },
  };
}

export async function adminListEcosystems(token: string): Promise<EcosystemAdminEntry[]> {
  return (await adminGetEcosystems(token)).ecosystems;
}

export async function adminUpdateRealmPackages(
  token: string,
  services: string[]
): Promise<string[]> {
  const data = await adminFetch('PATCH', '/api/admin/realm/packages', token, { services });
  return (data as { services: string[] }).services;
}

export interface CreateEcosystemPayload {
  id: string;
  displayName: string;
  logo?: string;
  memberGroup?: string;
  giteaOrg?: string;
  services?: string[];
  palette?: Record<string, string>;
}

export async function adminCreateEcosystem(token: string, payload: CreateEcosystemPayload): Promise<EcosystemAdminEntry> {
  const data = await adminFetch('POST', '/api/admin/ecosystems', token, payload);
  clearEcosystemsCache();
  return (data as { ecosystem: EcosystemAdminEntry }).ecosystem;
}

export interface UpdateEcosystemPayload {
  displayName?: string;
  logo?: string;
  giteaOrg?: string;
  services?: string[];
  palette?: Record<string, string>;
}

export async function adminUpdateEcosystem(token: string, id: string, payload: UpdateEcosystemPayload): Promise<EcosystemAdminEntry> {
  const data = await adminFetch('PATCH', `/api/admin/ecosystems/${encodeURIComponent(id)}`, token, payload);
  clearEcosystemsCache();
  return (data as { ecosystem: EcosystemAdminEntry }).ecosystem;
}

export async function adminRenameEcosystem(
  token: string,
  id: string,
  newId: string
): Promise<EcosystemAdminEntry> {
  const data = await adminFetch(
    'POST',
    `/api/admin/ecosystems/${encodeURIComponent(id)}/rename`,
    token,
    { newId }
  );
  clearEcosystemsCache();
  return (data as { ecosystem: EcosystemAdminEntry }).ecosystem;
}

export async function adminDecommissionEcosystem(token: string, id: string): Promise<void> {
  await adminFetch('POST', `/api/admin/ecosystems/${encodeURIComponent(id)}/decommission`, token);
  clearEcosystemsCache();
}

export async function adminReactivateEcosystem(token: string, id: string): Promise<void> {
  await adminFetch('POST', `/api/admin/ecosystems/${encodeURIComponent(id)}/reactivate`, token);
  clearEcosystemsCache();
}

export async function adminListEcosystemGroupMembers(
  token: string,
  ecosystemId: string
): Promise<{ members: EcosystemGroupMember[]; memberGroup: string }> {
  const data = await adminFetch(
    'GET',
    `/api/admin/ecosystems/${encodeURIComponent(ecosystemId)}/group-members`,
    token
  );
  return data as { members: EcosystemGroupMember[]; memberGroup: string };
}

export async function adminAddEcosystemGroupMember(
  token: string,
  ecosystemId: string,
  email: string
): Promise<void> {
  await adminFetch('POST', `/api/admin/ecosystems/${encodeURIComponent(ecosystemId)}/group-members`, token, {
    email,
  });
}

export async function adminRemoveEcosystemGroupMember(
  token: string,
  ecosystemId: string,
  body: { email?: string; userId?: string }
): Promise<void> {
  await adminFetch(
    'DELETE',
    `/api/admin/ecosystems/${encodeURIComponent(ecosystemId)}/group-members`,
    token,
    body
  );
}
